import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Tooltip, useMediaQuery, Drawer, Collapse } from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  LocalHospital as ClinicsIcon,
  Person as ProvidersIcon,
  Medication as ProductsIcon,
  MergeType as CombineIcon,
  Assignment as SubmissionsIcon,
  ShoppingCart as OrdersIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Business as CompaniesIcon,
  Assessment as AnalyticsIcon,
  BarChart as ReportsIcon,
  QueryStats as ActivityIcon,
  Folder as ResourcesIcon,
  ContactPhone as CRMIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Description as DocumentIcon,
  FolderSpecial as AdminDocsIcon,
  Email as EmailIcon,
  SupervisorAccount as UsersIcon,
  ReceiptLong as BillingIcon,
  VerifiedUser as AttestationIcon,
  Factory as ManufacturersIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import { displayRole } from '../../utils/roleDisplayLabels';
import {
  isPlatformAdmin,
  isClient as isClientRole,
  isClientAdmin as isClientAdminRole,
  isRep as isRepRole,
} from '../../utils/roleHelpers';
import { SessionTimeoutProvider } from '../../contexts/SessionTimeoutContext';
import { useTheme } from '../../context/ThemeContext';
import { useUser } from '../../contexts/UserContext';
import LogoIcon from '../common/MedNecessityLogo';
import './Layout.css';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  dividerAfter?: boolean;
}

/* ─── Inline svg icons (chevrons + hairline glyphs) ─── */
const ChevLeft = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 3.5L5.5 8 10 12.5" />
  </svg>
);
const ChevUp = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3.5 10L8 5.5 12.5 10" />
  </svg>
);
const ChevDown = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3.5 6L8 10.5 12.5 6" />
  </svg>
);
const BurgerSvg = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 4h12M2 8h12M2 12h12" />
  </svg>
);

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(min-width: 769px) and (max-width: 1200px)');
  const isDesktop = useMediaQuery('(min-width: 1201px)');

  const [collapsed, setCollapsed] = useState(!isDesktop);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(true);
  const [clientAdminExpanded, setClientAdminExpanded] = useState(true);

  React.useEffect(() => {
    if (isDesktop) setCollapsed(false);
    else if (isTablet) setCollapsed(true);
  }, [isDesktop, isTablet]);

  const { user, setUser } = useUser();
  // Role checks are case-insensitive so the navigation does not silently
  // break if a backend record stores 'admin' (lowercase) — see roleHelpers.ts
  // for the rationale.
  const isAdmin = isPlatformAdmin(user);
  const isClient = isClientRole(user);
  const isClientAdmin = isClientAdminRole(user);
  const isRep = isRepRole(user);
  const userName = user?.Name || user?.Username || 'User';
  const userInitial = userName.charAt(0).toUpperCase();

  const getMainNavItems = (): NavItem[] => {
    if (isRep) {
      return [
        { id: 'submissions', label: 'My Submissions', icon: <SubmissionsIcon />, path: '/rep/crm' },
        { id: 'orders', label: 'My Orders', icon: <OrdersIcon />, path: '/rep/orders' },
        { id: 'products', label: 'Products', icon: <ProductsIcon />, path: '/rep/products' },
        { id: 'resources', label: 'Resources', icon: <ResourcesIcon />, path: '/rep/resources' },
      ];
    }
    if (isClient || isClientAdmin) {
      return [
        { id: 'patients', label: 'Patients', icon: <PeopleIcon />, path: '/client/patients' },
        { id: 'clinics', label: 'Clinics', icon: <ClinicsIcon />, path: '/client/clinics' },
        { id: 'providers', label: 'Providers', icon: <ProvidersIcon />, path: '/client/providers' },
        { id: 'products', label: 'Products', icon: <ProductsIcon />, path: '/products', dividerAfter: true },
        { id: 'submissions', label: 'Submissions', icon: <SubmissionsIcon />, path: '/client/crm' },
        { id: 'resources', label: 'Resources', icon: <ResourcesIcon />, path: '/client/resources' },
      ];
    }
    // Clinics is promoted up so it's right under Dashboard — admins land
    // on this surface most often when working an account. Submissions and
    // Orders sit in the base set because every operational role touches
    // them; the genuinely admin-only items live in the Admin section below.
    return [
      { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
      { id: 'clinics', label: 'Clinics', icon: <ClinicsIcon />, path: '/clinics' },
      { id: 'patients', label: 'Patients', icon: <PeopleIcon />, path: '/patients' },
      { id: 'providers', label: 'Providers', icon: <ProvidersIcon />, path: '/physicians' },
      { id: 'submissions', label: 'Submissions', icon: <SubmissionsIcon />, path: '/admin/unified-submissions' },
      { id: 'orders', label: 'Orders', icon: <OrdersIcon />, path: '/admin/orders' },
      { id: 'products', label: 'Products', icon: <ProductsIcon />, path: '/products' },
      { id: 'combine-pdf', label: 'Document Combiner', icon: <CombineIcon />, path: '/tools/combine-pdf' },
    ];
  };

  // Admin-only nav. Anything an Admin specifically needs (and other roles
  // shouldn't see) lives here, behind the collapsible "Admin" section.
  const adminNavItems: NavItem[] = isAdmin ? [
    { id: 'management', label: 'Management', icon: <CompaniesIcon />, path: '/admin/management' },
    { id: 'organizations', label: 'Manufacturers', icon: <ManufacturersIcon />, path: '/admin/organizations' },
    { id: 'analytics', label: 'Analytics', icon: <AnalyticsIcon />, path: '/admin/analytics' },
    { id: 'crm', label: 'CRM', icon: <CRMIcon />, path: '/admin/crm' },
    { id: 'reports', label: 'Reports', icon: <ReportsIcon />, path: '/admin/reports' },
    { id: 'billing', label: 'Billing', icon: <BillingIcon />, path: '/admin/billing' },
    { id: 'attestations', label: 'Attestations', icon: <AttestationIcon />, path: '/admin/attestations' },
    { id: 'employee-activity', label: 'System Diagnostics', icon: <ActivityIcon />, path: '/admin/system-diagnostics' },
    { id: 'client-resources', label: 'MedNec Resources', icon: <DocumentIcon />, path: '/admin/client-resources' },
    { id: 'admin-documents', label: 'Admin Documents', icon: <AdminDocsIcon />, path: '/admin/documents' },
    { id: 'email', label: 'Email', icon: <EmailIcon />, path: '/admin/emails' },
  ] : [];

  const clientAdminItems: NavItem[] = isClientAdmin ? [
    // Single Management entry point — opens the tabbed page that hosts
    // Explorer / Partner Onboarding / User Onboarding for the affiliate.
    { id: 'admin-management', label: 'Management', icon: <UsersIcon />, path: '/client-admin/management' },
    { id: 'admin-reports', label: 'Reports', icon: <ReportsIcon />, path: '/client-admin/reports' },
  ] : [];

  const mainNavItems = getMainNavItems();

  const handleLogout = async () => {
    try {
      await api.post('/api/logout', {}, { withCredentials: true });
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setUser(null);
      navigate('/login', { replace: true });
    }
  };

  const isActivePath = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const navWidth = collapsed ? 64 : 240;

  /* ─── Render a single nav item (with optional collapsed-state tooltip) ─── */
  const renderNavItem = (item: NavItem, key?: string | number) => {
    const active = isActivePath(item.path);
    const button = (
      <button
        type="button"
        className={`mn-nav-item${active ? ' is-active' : ''}`}
        onClick={() => handleNavigation(item.path)}
      >
        <span className="mn-nav-item-icon">{item.icon}</span>
        <span className="mn-nav-item-label">{item.label}</span>
      </button>
    );
    if (collapsed && !isMobile) {
      return (
        <Tooltip key={key ?? item.id} title={item.label} placement="right">
          {button}
        </Tooltip>
      );
    }
    return <React.Fragment key={key ?? item.id}>{button}</React.Fragment>;
  };

  /* ─── Sidebar contents (used for desktop fixed pane + mobile drawer) ─── */
  const isCollapsed = collapsed && !isMobile;
  const handleBrandClick = () => {
    if (isCollapsed) setCollapsed(false);
    else navigate('/dashboard');
  };
  const NavContent = () => {
    const brandMark = (
      <div
        className="mn-nav-brand-mark"
        onClick={handleBrandClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleBrandClick(); } }}
      >
        <LogoIcon size={26} color="currentColor" className="mn-nav-brand-logo" />
        <span className="mn-nav-brand-text">MedNecessity</span>
      </div>
    );
    return (
    <nav className={`mn-nav${isCollapsed ? ' is-collapsed' : ''}${isDarkMode ? ' dark' : ''}`}>
      {/* Brand */}
      <div className="mn-nav-brand">
        {isCollapsed ? (
          <Tooltip title="Expand sidebar" placement="right">{brandMark}</Tooltip>
        ) : brandMark}
        {!isMobile && !collapsed && (
          <button
            type="button"
            className="mn-nav-collapse"
            onClick={() => setCollapsed(true)}
            aria-label="Collapse sidebar"
          >
            <ChevLeft />
          </button>
        )}
      </div>

      {/* Main list */}
      <div className="mn-nav-list">
        {mainNavItems.map((item) => (
          <React.Fragment key={item.id}>
            {renderNavItem(item)}
            {item.dividerAfter && (
              <div className="mn-nav-section" aria-hidden>
                <span className="mn-nav-section-rule" />
              </div>
            )}
          </React.Fragment>
        ))}

        {/* Admin section */}
        {adminNavItems.length > 0 && (
          <>
            {(!collapsed || isMobile) ? (
              <button
                type="button"
                className="mn-nav-section is-clickable"
                onClick={() => setAdminExpanded((v) => !v)}
                aria-expanded={adminExpanded}
              >
                <span>Admin</span>
                <span className="mn-nav-section-rule" />
                <span className="mn-nav-section-chev">
                  {adminExpanded ? <ChevUp /> : <ChevDown />}
                </span>
              </button>
            ) : (
              <div className="mn-nav-section" aria-hidden>Admin</div>
            )}
            <Collapse in={adminExpanded || (collapsed && !isMobile)} timeout="auto" unmountOnExit>
              {adminNavItems.map((item) => (
                <React.Fragment key={item.id}>{renderNavItem(item)}</React.Fragment>
              ))}
            </Collapse>
          </>
        )}

        {/* Client Admin section */}
        {clientAdminItems.length > 0 && (
          <>
            {(!collapsed || isMobile) ? (
              <button
                type="button"
                className="mn-nav-section is-clickable"
                onClick={() => setClientAdminExpanded((v) => !v)}
                aria-expanded={clientAdminExpanded}
              >
                <span>Client Admin</span>
                <span className="mn-nav-section-rule" />
                <span className="mn-nav-section-chev">
                  {clientAdminExpanded ? <ChevUp /> : <ChevDown />}
                </span>
              </button>
            ) : (
              <div className="mn-nav-section" aria-hidden>C / Admin</div>
            )}
            <Collapse in={clientAdminExpanded || (collapsed && !isMobile)} timeout="auto" unmountOnExit>
              {clientAdminItems.map((item) => (
                <React.Fragment key={item.id}>{renderNavItem(item)}</React.Fragment>
              ))}
            </Collapse>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="mn-nav-foot">
        {/* Theme toggle */}
        {(() => {
          const themeBtn = (
            <button
              type="button"
              className="mn-nav-theme"
              onClick={toggleTheme}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <span className="mn-nav-theme-icon">
                {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </span>
              <span className="mn-nav-theme-text">{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
              <span className={`mn-nav-theme-switch${isDarkMode ? ' is-on' : ''}`} aria-hidden />
            </button>
          );
          return collapsed && !isMobile ? (
            <Tooltip title={isDarkMode ? 'Light Mode' : 'Dark Mode'} placement="right">
              {themeBtn}
            </Tooltip>
          ) : themeBtn;
        })()}

        {/* Settings — re-uses nav-item class */}
        {renderNavItem({ id: 'settings', label: 'Settings', icon: <SettingsIcon />, path: '/settings' })}

        {/* User profile */}
        {(() => {
          const userBtn = (
            <button type="button" className="mn-nav-user" onClick={handleLogout} aria-label="Sign out">
              <span className="mn-nav-user-avatar">{userInitial}</span>
              <span className="mn-nav-user-meta">
                <span className="mn-nav-user-name">{userName}</span>
                <span className="mn-nav-user-role">{displayRole(user?.Role || 'User')}</span>
              </span>
              <span className="mn-nav-user-logout">
                <LogoutIcon style={{ fontSize: 16 }} />
              </span>
            </button>
          );
          return collapsed && !isMobile ? (
            <Tooltip title={`Sign out — ${userName}`} placement="right">
              {userBtn}
            </Tooltip>
          ) : userBtn;
        })()}
      </div>
    </nav>
    );
  };

  return (
    <SessionTimeoutProvider>
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--surface-onyx)' }}>
        {/* Mobile top bar */}
        {isMobile && (
          <div className={`mn-shell-mobile-bar${isDarkMode ? ' dark' : ''}`}>
            <button
              type="button"
              className="mn-shell-mobile-burger"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation"
            >
              <BurgerSvg />
            </button>
            <div className="mn-shell-mobile-brand">
              <LogoIcon size={22} color="currentColor" className="mn-shell-mobile-brand-logo" />
              <span>MedNecessity</span>
            </div>
          </div>
        )}

        {/* Desktop sidebar (fixed) */}
        {!isMobile && (
          <div style={{ position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 1100 }}>
            <NavContent />
          </div>
        )}

        {/* Mobile drawer */}
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              bgcolor: 'transparent',
              border: 'none',
              borderRadius: 0,
              backgroundImage: 'none',
            },
          }}
        >
          <NavContent />
        </Drawer>

        {/* Main content */}
        <main
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            overflow: 'auto',
            marginLeft: isMobile ? 0 : `${navWidth}px`,
            paddingTop: isMobile ? '56px' : 0,
            transition: 'margin-left 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <Outlet />
        </main>
      </div>
    </SessionTimeoutProvider>
  );
};

export default Layout;
